import React from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { get } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateRoleModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(45)
        .required(),
    description: Yup.string()
        .max(100)
        .required()
});

const CreateRoleModal = ({
    close,
    clearErrors,
    open,
    title,
    isLoading,
    toggleLoading,
    errors,
    ...props
}) => {
    const formik = useFormik({
        initialValues: {
            name:        '',
            description: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                companyInfo,
                create,
                fail
            } = props;

            const roles = get(companyInfo, 'roles', []);
            const doesRoleExist = roles.find(role => role.name === values.name);

            if (doesRoleExist) {
                fail('Role name already exists');
                setSubmitting(false);
                toggleLoading(false);

                return;
            }

            let isCreated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = create(values);
                }

                if (isCreated) {
                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    return (
        <EsmsDialog
            onClose={close}
            disabled={formik.isSubmitting}
            onConfirm={formik.handleSubmit}
            confirmText="Create"
            open={open}
            isLoading={isLoading}
            title={title}
            cancelable={true}
            errors={errors}
            onRendered={formik.handleReset}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/rolesFlat.svg" alt="Roles Icon" />
                        <span>New Role</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Provide a unique role name.
                        </p>
                        <p>
                            Description is required.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.fullWidth}>
                            <TextField
                                id="description"
                                name="description"
                                type="text"
                                value={formik.values.description}
                                label="Description"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.description) && Boolean(formik.touched.description)}
                                helperText={Boolean(formik.touched.description) && formik.errors.description}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateRoleModal.defaultProps = {
    clearErrors: () => {},
    close:       () => {},
    companyInfo: {},
    create:      () => {},
    errors:      [],
    fail:        () => {},
    isLoading:   false,
    open:        false,
    title:       ''
};

CreateRoleModal.propTypes = {
    clearErrors: func,
    close:       func,
    companyInfo: object,
    create:      func,
    errors:      array,
    fail:        func,
    isLoading:   bool,
    open:        bool,
    title:       string
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors,
        isLoading:   state.ui.isLoading
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateRoleModal);
